/**
 * SidebarNav — Dashboard sidebar with brand area, app switcher, and grouped navigation.
 * Visual parity with Portal's Sidebar.astro — same brand area, AppSwitcher pills,
 * section accordions, and icon-only collapsed mode.
 * @layer widget
 * @package @plerity/dashboard-shared
 */

import { clsx } from "clsx";
import { type ComponentType, type ReactNode, Suspense, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { type AppEntry, getSsrApps } from "../../config/app-registry";
import type { NavItem, NavSection } from "../../types/dashboard";
import { OverlayScrollbar } from "./OverlayScrollbar";

/**
 * F-O2 (finance_app_quality_plan_2026-04-25): nav icons can be either eager
 * (`import { Foo } from 'lucide-react'`) OR lazy (`React.lazy(() => import(...))`).
 * Wrapping the icon render site in `<Suspense>` with a same-size placeholder
 * lets apps opt-in to deferred icon chunks without further shared-package
 * changes. Eager icons render synchronously — Suspense is a no-op for them.
 *
 * Placeholder is `inline-block` with the same `h-4 w-4 shrink-0` footprint so
 * row layout doesn't shift while a lazy chunk resolves.
 */
function NavIcon({ icon: Icon }: { icon?: ComponentType<{ className?: string; "aria-hidden"?: "true" | "false" }> }) {
  if (!Icon) return null;
  return (
    <Suspense fallback={<span aria-hidden="true" className="inline-block h-4 w-4 shrink-0" />}>
      <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
    </Suspense>
  );
}

// Brand icon — imported as Vite asset (resolves to URL at build time).
// To swap for a different business: replace this PNG file.
import brandIconUrl from "../../assets/brand-icon.png";

// ============================================================================
// Types
// ============================================================================

/** @deprecated Use `AppEntry` from `@plerity/dashboard-shared/config/app-registry` */
export type AppSwitcherEntry = AppEntry;

export interface SidebarBrand {
  /** Logo image src */
  logo: string;
  /** Alt text for logo */
  logoAlt?: string;
  /** Brand title text */
  title: string;
  /** Link target for brand click */
  href?: string;
}

export interface SidebarNavProps {
  sections: NavSection[];
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Brand/logo area at top of sidebar */
  brand?: SidebarBrand;
  /** App switcher entries (Portal, CRM, Builder) */
  apps?: AppSwitcherEntry[];
  /** Footer items (rendered below nav, above collapse button) */
  footer?: ReactNode;
  className?: string;
}

// ============================================================================
// Default app switcher entries — sourced from APP_REGISTRY
// ============================================================================

/** @deprecated Use `getSsrApps()` from `@plerity/dashboard-shared/config/app-registry` */
export const DEFAULT_APPS: AppSwitcherEntry[] = getSsrApps();

/**
 * DEFAULT_BRAND — centralized brand config for all dashboard shells.
 * To swap for a different business:
 *   1. Replace the PNG at dashboard-shared/assets/brand-icon.png, OR
 *   2. Override `brand.logo` in any shell's createShell() config
 */
export const DEFAULT_BRAND: SidebarBrand = {
  logo: brandIconUrl,
  logoAlt: "Animas Marketing",
  title: "Animas",
  href: "/",
};

// ============================================================================
// Sub-components
// ============================================================================

function AppSwitcher({ apps, collapsed }: { apps: AppSwitcherEntry[]; collapsed: boolean }) {
  if (collapsed) return null;

  return (
    <nav className="flex flex-wrap gap-1 border-b border-light px-4 py-3" aria-label="Switch application">
      {apps.map((app) =>
        app.active ? (
          <span
            key={app.id}
            className="inline-flex items-center rounded px-2 py-1 text-xs font-semibold bg-brand-50 text-brand-800 border border-brand-200 dark:bg-brand-900/30 dark:text-brand-300 dark:border-brand-800"
            aria-current="page"
          >
            {app.label}
          </span>
        ) : (
          <a
            key={app.id}
            href={app.url}
            className="inline-flex items-center rounded border border-gray-200 px-2 py-1 text-xs font-medium text-muted transition-colors hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300 dark:border-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200 dark:hover:border-slate-500"
            title={`Go to ${app.label}`}
          >
            {app.label}
          </a>
        )
      )}
    </nav>
  );
}

function BrandArea({ brand, collapsed }: { brand: SidebarBrand; collapsed: boolean }) {
  const content = (
    <>
      {brand.logo && (
        <img
          src={brand.logo}
          alt={brand.logoAlt ?? brand.title}
          width={28}
          height={28}
          className="shrink-0 rounded-full"
        />
      )}
      {!collapsed && <span className="text-base font-semibold text-fg truncate">{brand.title}</span>}
    </>
  );

  const className = clsx(
    "flex items-center gap-3 border-b border-light",
    collapsed ? "justify-center px-2 py-4" : "px-5 py-4"
  );

  if (brand.href) {
    return (
      <a href={brand.href} className={clsx(className, "no-underline text-inherit")}>
        {content}
      </a>
    );
  }

  return <div className={className}>{content}</div>;
}

/** Normalise a badge value: empty / zero → null; numbers > 99 → "99+". */
export function formatNavBadge(value: string | number | null | undefined): string | null {
  if (value == null || value === "" || value === 0) return null;
  if (typeof value === "number") return value > 99 ? "99+" : String(value);
  return value;
}

const BADGE_PILL_CLASS =
  "ml-auto rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium dark:bg-slate-700 dark:text-slate-300";

/** Pill (expanded) or dot (collapsed) for a resolved badge value. */
function BadgeMark({ value, collapsed, className }: { value: string | null; collapsed: boolean; className?: string }) {
  if (value == null) return null;
  if (collapsed) {
    return (
      <span
        data-testid="nav-badge-dot"
        className="absolute top-1 right-1 h-2 w-2 rounded-full bg-brand-500 dark:bg-brand-400"
      >
        <span className="sr-only">{value}</span>
      </span>
    );
  }
  return (
    <span data-testid="nav-badge" className={clsx(BADGE_PILL_CLASS, className)}>
      {value}
    </span>
  );
}

/** Calls the item's live `useBadge` hook — isolated so hook order is stable per item. */
function LiveNavBadge({
  useBadge,
  collapsed,
  className,
}: {
  useBadge: NonNullable<NavItem["useBadge"]>;
  collapsed: boolean;
  className?: string;
}) {
  const value = useBadge();
  return <BadgeMark value={formatNavBadge(value)} collapsed={collapsed} className={className} />;
}

/**
 * Sidebar badge for a nav item. Live (`useBadge`) wins over static (`badge`).
 * Static badges keep their historical behaviour: shown only when expanded,
 * rendered verbatim (including `0`).
 */
function NavBadge({ item, collapsed, className }: { item: NavItem; collapsed: boolean; className?: string }) {
  if (item.useBadge) {
    return <LiveNavBadge useBadge={item.useBadge} collapsed={collapsed} className={className} />;
  }
  if (collapsed || item.badge == null) return null;
  return (
    <span data-testid="nav-badge" className={clsx(BADGE_PILL_CLASS, className)}>
      {item.badge}
    </span>
  );
}

/** Check if any child path matches the current location */
function hasActiveChild(children: NavItem[], pathname: string): boolean {
  return children.some((child) => pathname === child.path || pathname.startsWith(`${child.path}/`));
}

/** Render a single flat nav link (used for items without children, and for child items) */
function NavItemLink({ item, collapsed, indented = false }: { item: NavItem; collapsed: boolean; indented?: boolean }) {
  const sharedClassName = clsx(
    "relative flex items-center rounded-md transition-colors",
    collapsed ? "justify-center mx-2 p-2" : indented ? "gap-2 py-1.5 pl-9 pr-3 text-sm" : "gap-2 px-3 py-2 text-sm",
    "text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700",
    item.disabled && "pointer-events-none opacity-50"
  );

  const sharedContent = (
    <>
      <NavIcon icon={item.icon} />
      {!collapsed && <span className="truncate">{item.label}</span>}
      <NavBadge item={item} collapsed={collapsed} />
    </>
  );

  // External route (href set): render as plain <a> — no react-router isActive
  // logic, no NavLink registration. Keyboard-accessible: Enter activates the link
  // naturally since it's a real anchor element.
  if (item.href) {
    return (
      <a href={item.href} title={collapsed ? item.label : undefined} className={sharedClassName} target="_self">
        {sharedContent}
      </a>
    );
  }

  return (
    <NavLink
      to={item.path}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        clsx(
          "relative flex items-center rounded-md transition-colors",
          collapsed
            ? "justify-center mx-2 p-2"
            : indented
              ? "gap-2 py-1.5 pl-9 pr-3 text-sm"
              : "gap-2 px-3 py-2 text-sm",
          isActive
            ? "bg-brand-50 font-medium text-brand-800 dark:bg-brand-900/30 dark:text-brand-300"
            : "text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700",
          item.disabled && "pointer-events-none opacity-50"
        )
      }
    >
      {sharedContent}
    </NavLink>
  );
}

/** Render a parent nav item with expandable children (QuickBooks-style sub-menu) */
function ExpandableNavItem({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { pathname } = useLocation();
  // Parent only renders ExpandableNavItem when item.children is non-empty.
  const children = item.children ?? [];
  const childActive = hasActiveChild(children, pathname);
  const [expanded, setExpanded] = useState(item.defaultExpanded || childActive);

  // Auto-expand when navigating to a child route
  if (childActive && !expanded) {
    setExpanded(true);
  }

  // In collapsed mode, just show the parent icon (children hidden)
  if (collapsed) {
    return (
      <NavLink
        to={item.path}
        title={item.label}
        className={({ isActive }) =>
          clsx(
            "relative flex items-center rounded-md transition-colors justify-center mx-2 p-2",
            isActive || childActive
              ? "bg-brand-50 font-medium text-brand-800 dark:bg-brand-900/30 dark:text-brand-300"
              : "text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700"
          )
        }
      >
        <NavIcon icon={item.icon} />
        <NavBadge item={item} collapsed />
      </NavLink>
    );
  }

  return (
    <div>
      {/* Parent item — click toggles children, acts as a link too */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={clsx(
          "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
          childActive
            ? "bg-brand-50 font-medium text-brand-800 dark:bg-brand-900/30 dark:text-brand-300"
            : "text-gray-700 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-slate-700"
        )}
        aria-expanded={expanded}
      >
        <NavIcon icon={item.icon} />
        <span className="truncate">{item.label}</span>
        <NavBadge item={item} collapsed={false} className="mr-1" />
        {/* Chevron indicator */}
        <svg
          aria-hidden="true"
          className={clsx(
            "ml-auto h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 dark:text-slate-500",
            expanded && "rotate-90"
          )}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Child items — indented sub-menu */}
      {expanded && (
        <div className="ml-2 border-l border-light">
          {children.map((child) => (
            <NavItemLink key={child.path} item={child} collapsed={false} indented />
          ))}
        </div>
      )}
    </div>
  );
}

function SectionGroup({ section, collapsed }: { section: NavSection; collapsed: boolean }) {
  const [open, setOpen] = useState(section.defaultOpen !== false);

  return (
    <div className="mb-2">
      {/* Section title — hidden when collapsed, and omitted entirely for
          title-less ("standalone") sections so their items render as
          top-level nav links with no group header. */}
      {!collapsed &&
        section.title &&
        (section.collapsible !== false ? (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="flex w-full items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider text-secondary hover:text-gray-700 dark:hover:text-slate-200"
          >
            {section.title}
            <span className={clsx("text-3xs transition-transform", open && "rotate-180")}>&#9660;</span>
          </button>
        ) : (
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-secondary">{section.title}</div>
        ))}

      {/* Nav items — show icons only when collapsed */}
      {(open || collapsed) && (
        <nav className="space-y-0.5">
          {section.items.map((item) =>
            item.children && item.children.length > 0 ? (
              <ExpandableNavItem key={item.path} item={item} collapsed={collapsed} />
            ) : (
              <NavItemLink key={item.path} item={item} collapsed={collapsed} />
            )
          )}
        </nav>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SidebarNav({
  sections,
  collapsed = false,
  onToggleCollapse,
  brand,
  apps,
  footer,
  className,
}: SidebarNavProps) {
  return (
    <aside
      aria-label="Main navigation"
      className={clsx(
        // Chrome surface: bg-surface (white light / #1e293b dark) elevated over
        // the bg-surface-page canvas — S1 fleet dark-mode decision 2026-07-07.
        "flex h-full flex-col border-r border-light border-l-4 border-l-brand-500 bg-surface dark:border-l-brand-400",
        // Desktop: sit in the layout's flex row, animate width on collapse.
        "md:relative md:transition-[width] md:duration-200 md:translate-x-0",
        collapsed ? "md:w-16" : "md:w-64",
        // Mobile (max-md): full-screen drawer that slides in from the left
        // when expanded and is hidden (off-canvas) when collapsed. Avoids
        // the cramped 311 px content area we'd get if the icon column stayed
        // visible at 375 px viewport width. Drawer width is w-72 (288 px) so
        // every tap target stays comfortably hittable on phones.
        "max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-40 max-md:transition-transform max-md:duration-200",
        collapsed ? "max-md:-translate-x-full max-md:w-72" : "max-md:translate-x-0 max-md:w-72 max-md:shadow-2xl",
        className
      )}
    >
      {/* Brand area */}
      {brand && <BrandArea brand={brand} collapsed={collapsed} />}

      {/* App switcher */}
      {apps && <AppSwitcher apps={apps} collapsed={collapsed} />}

      {/* Navigation sections */}
      <OverlayScrollbar className="p-2" testId="sidebar-nav">
        {sections.map((section, idx) => (
          <SectionGroup key={section.title || `section-${idx}`} section={section} collapsed={collapsed} />
        ))}
      </OverlayScrollbar>

      {/* Footer area (settings, etc.) */}
      {footer && !collapsed && <div className="border-t border-light p-2">{footer}</div>}

      {/* Collapse toggle */}
      {onToggleCollapse && (
        <button
          type="button"
          onClick={onToggleCollapse}
          className="flex h-10 items-center justify-center border-t border-light text-gray-400 transition-colors hover:text-secondary"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? "\u00BB" : "\u00AB"}
        </button>
      )}
    </aside>
  );
}
